import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
// Removed User import as specific fields are passed via location prop
// import { User } from '../../services/userService';

// Fix default Leaflet icon issue in React
// @ts-ignore - Deleting private property as workaround for icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Helper component to dynamically change map view
const ChangeView: React.FC<{ center: L.LatLngExpression; zoom: number }> = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
};

interface LocationMapProps {
    location: {
        location_name?: string; // Received from parent
        coordinates?: { lat: number; lng: number }; // Received from parent
    };
}

const LocationMap: React.FC<LocationMapProps> = ({ location }) => {
    const { coordinates, location_name } = location;
    const defaultCenter: L.LatLngExpression = [40.7128, -74.0060]; // Default (e.g., New York) if no coords
    const mapCenter: L.LatLngExpression = coordinates ? [coordinates.lat, coordinates.lng] : defaultCenter;
    const zoomLevel = 12;
    const circleRadius = 3000; // 3km

    // State to ensure map container is ready before initialization
    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true); // Set true only after component mounts
    }, []);

    // Custom marker icon resembling the original HTML/CSS
    const customMarkerIcon = L.divIcon({
        html: `<span style="background-color: #3498db; width: 16px; height: 16px; border-radius: 50%; display: block; border: 2px solid #fff; box-shadow: 0 0 0 1px #3498db;"></span>`,
        className: '', // No default wrapper class
        iconSize: [16, 16],
        iconAnchor: [8, 8] // Center the marker
    });

    return (
        <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
                <h5 className="mb-0 fw-bold">My Location</h5>
            </div>
            <div className="card-body p-0">
                <div style={{ height: '250px', width: '100%' }}>
                    {/* Only render MapContainer on the client-side */}
                    {isClient ? (
                        <MapContainer
                            center={mapCenter}
                            zoom={zoomLevel}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={false}
                        >
                            <ChangeView center={mapCenter} zoom={zoomLevel} />
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            {coordinates && (
                                <>
                                    <Marker position={mapCenter} icon={customMarkerIcon} />
                                    <Circle
                                        center={mapCenter}
                                        radius={circleRadius}
                                        pathOptions={{
                                            color: 'rgba(52, 152, 219, 0.5)',       
                                            fillColor: 'rgba(52, 152, 219, 0.1)',   
                                            fillOpacity: 0.5,
                                            weight: 1
                                        }}
                                    />
                                </> 
                            )}
                        </MapContainer>
                    ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa' }}>
                            Loading map...
                        </div>
                    )}
                </div>
                <div className="p-3">
                    <p className="text-muted small mb-0">
                        <span className="me-2">ℹ️</span>
                        For privacy, only your approximate location is shown
                        {location_name && ` (${location_name})`}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LocationMap; 